import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import { useEffect } from 'react';
import { sanitiseRichText, buildRichEditorExtensions } from './richEditorConfig';

/**
 * Rich-text editor for adviser narrative fields. Backed by TipTap, configured
 * with StarterKit (headings, lists, bold/italic, undo/redo, link) +
 * Placeholder. Emits sanitised HTML to `onChange`.
 *
 * Same prop interface as the previous contentEditable shim so consumers
 * (Profile tab's Goals / Strategy / Personal history fields) don't change.
 *
 * The extension stack and the `sanitiseRichText` sanitiser live in the
 * React-free `./richEditorConfig` module so they can be unit tested headlessly.
 * `sanitiseRichText` is re-exported here for back-compat with existing callers.
 */
export { sanitiseRichText } from './richEditorConfig';

export function RichEditor({
  value,
  onChange,
  placeholder,
  minHeight = 120,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}) {
  const editor = useEditor({
    extensions: buildRichEditorExtensions(placeholder),
    // TipTap v3 stopped re-rendering on every transaction by default; the
    // toolbar's active-state highlighting relies on it, so opt back in.
    shouldRerenderOnTransaction: true,
    content: sanitiseRichText(value ?? ''),
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(sanitiseRichText(html));
    },
    editorProps: {
      attributes: {
        class: 'qa-input prose prose-sm max-w-none outline-none focus:ring-0',
        style: `min-height: ${minHeight}px; border-radius: 0; border-color: transparent;`,
      },
    },
  });

  // Keep editor in sync if parent updates value externally (e.g. server hydrate)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value ?? '') !== current) editor.commands.setContent(sanitiseRichText(value ?? ''), { emitUpdate: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return <div className="qa-card" style={{ minHeight }} />;

  return (
    <div className="qa-card">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  function insertLink() {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    if (!/^(https?:|mailto:)/i.test(url)) return;
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  const isActive = (name: string, attrs?: Record<string, unknown>) => editor.isActive(name, attrs);

  return (
    <div className="flex flex-wrap gap-1 border-b border-hairline-paper px-2 py-1 text-xs qa-mono">
      <Btn label="B" cls="font-bold" active={isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
      <Btn label="I" cls="italic" active={isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <Btn label="H2" active={isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      <Btn label="H3" active={isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
      <Btn label="¶" active={isActive('paragraph')} onClick={() => editor.chain().focus().setParagraph().run()} />
      <Btn label="•" active={isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <Btn label="1." active={isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <Btn label="🔗" active={isActive('link')} onClick={insertLink} />
      <Btn label="↺" onClick={() => editor.chain().focus().undo().run()} />
      <Btn label="↻" onClick={() => editor.chain().focus().redo().run()} />
    </div>
  );
}

function Btn({ label, onClick, active, cls = '' }: { label: string; onClick: () => void; active?: boolean; cls?: string }) {
  return (
    <button
      type="button"
      onMouseDown={e => { e.preventDefault(); onClick(); }}
      className={`px-2 py-1 ${cls} ${active ? 'text-rust' : 'text-ink-mute hover:text-ink'} hover:bg-rust/5`}
      aria-pressed={!!active}
    >
      {label}
    </button>
  );
}
